import { useCallback, useLayoutEffect, useRef } from 'react';

import { INACTIVITY_MS } from '../utils/thresholds.ts';

type UseInactivityTimerParams = {
	enabled: boolean;
	session: object;
	onElapsed: () => void;
};

type UseInactivityTimerResult = {
	clear: () => void;
	restart: () => void;
};

/** Owns only inactivity timing and committed-session cleanup. */
export function useInactivityTimer({
	enabled,
	session,
	onElapsed,
}: UseInactivityTimerParams): UseInactivityTimerResult {
	const activeSessionRef = useRef<object | null>(null);
	const onElapsedRef = useRef(onElapsed);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useLayoutEffect(() => {
		onElapsedRef.current = onElapsed;
	}, [onElapsed]);

	const clearActiveTimer = useCallback(() => {
		if (timerRef.current !== null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	useLayoutEffect(() => {
		activeSessionRef.current = session;
		if (!enabled) {
			clearActiveTimer();
		}

		return () => {
			if (activeSessionRef.current === session) {
				activeSessionRef.current = null;
			}
			clearActiveTimer();
		};
	}, [clearActiveTimer, enabled, session]);

	const clear = useCallback(() => {
		if (activeSessionRef.current === session) {
			clearActiveTimer();
		}
	}, [clearActiveTimer, session]);

	const restart = useCallback(() => {
		if (!enabled || activeSessionRef.current !== session) {
			return;
		}

		clearActiveTimer();
		timerRef.current = setTimeout(() => {
			if (activeSessionRef.current !== session) {
				return;
			}

			timerRef.current = null;
			onElapsedRef.current();
		}, INACTIVITY_MS);
	}, [clearActiveTimer, enabled, session]);

	return { clear, restart };
}
